import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import vipService from '../services/vipService';

/**
 * VIP Program Management
 * 
 * Comprehensive VIP tier management including:
 * - Tier configuration
 * - Benefits management
 * - Member tracking
 * - Program analytics
 */

const DEFAULT_TIERS = [
  { id: 'bronze', name: 'Bronze', minSpend: 0, color: '#cd7f32', icon: '🥉' },
  { id: 'silver', name: 'Silver', minSpend: 500, color: '#c0c0c0', icon: '🥈' },
  { id: 'gold', name: 'Gold', minSpend: 2000, color: '#ffd700', icon: '🥇' },
  { id: 'platinum', name: 'Platinum', minSpend: 5000, color: '#e5e4e2', icon: '💎' },
  { id: 'diamond', name: 'Diamond', minSpend: 10000, color: '#b9f2ff', icon: '👑' }
];

const BENEFIT_TYPES = [
  { id: 'discount', name: 'Discount', icon: '🏷️', unit: '%' },
  { id: 'free_shipping', name: 'Free Shipping', icon: '🚚', unit: 'boolean' },
  { id: 'early_access', name: 'Early Access', icon: '⏰', unit: 'days' },
  { id: 'points_multiplier', name: 'Points Multiplier', icon: '✨', unit: 'x' },
  { id: 'exclusive_products', name: 'Exclusive Products', icon: '🎁', unit: 'boolean' },
  { id: 'priority_support', name: 'Priority Support', icon: '🎯', unit: 'boolean' },
  { id: 'birthday_bonus', name: 'Birthday Bonus', icon: '🎂', unit: '%' },
  { id: 'cashback', name: 'Cashback', icon: '💰', unit: '%' }
];

const VIPProgram = () => {
  const navigate = useNavigate();
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [customers, setCustomers] = useState([]);
  const [programStatus, setProgramStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [showTierModal, setShowTierModal] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // Form states
  const [tierForm, setTierForm] = useState({
    name: '',
    minSpend: '',
    color: '#6b7280',
    icon: '⭐',
    benefits: {}
  });

  const [benefitsForm, setBenefitsForm] = useState({});

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersData, customersData, statusData, analyticsData] = await Promise.all([
        vipService.getTiers().catch(() => ({ tiers: DEFAULT_TIERS })),
        vipService.getCustomers({ limit: 50 }).catch(() => ({ customers: [] })),
        vipService.getStatus().catch(() => ({ active: false, total_members: 0 })),
        vipService.getAnalytics().catch(() => null)
      ]);

      setTiers(tiersData.tiers || DEFAULT_TIERS);
      setCustomers(customersData.customers || []);
      setProgramStatus(statusData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error fetching VIP data:', err);
      setError('Failed to load VIP program data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Configure tiers
  const handleConfigureTiers = async () => {
    setLoading(true);
    try {
      await vipService.configureTiers({ tiers });
      setSuccess('Tier configuration saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save tier configuration');
    } finally {
      setLoading(false);
    }
  };

  // Configure benefits
  const handleConfigureBenefits = async () => {
    if (!selectedTier) return;
    
    setLoading(true);
    try {
      await vipService.configureBenefits({
        tier_id: selectedTier.id,
        benefits: benefitsForm
      });
      setShowBenefitsModal(false);
      setSuccess('Benefits configured successfully');
      fetchData();
    } catch (err) {
      setError('Failed to configure benefits');
    } finally {
      setLoading(false);
    }
  };

  // Launch program
  const handleLaunchProgram = async () => {
    setLoading(true);
    try {
      await vipService.launchProgram({
        tiers,
        start_date: new Date().toISOString(),
        welcome_message: 'Welcome to our VIP program!'
      });
      setSuccess('VIP program launched successfully!');
      fetchData();
    } catch (err) {
      setError('Failed to launch VIP program');
    } finally {
      setLoading(false);
    }
  };

  // Update customer tier
  const handleUpdateCustomerTier = async (customerId, tierId) => {
    try {
      await vipService.updateCustomerTier(customerId, tierId, 'Manual tier update');
      setSuccess('Customer tier updated');
      fetchData();
    } catch (err) {
      setError('Failed to update customer tier');
    }
  };

  // Render tier card
  const renderTierCard = (tier, index) => (
    <div
      key={tier.id}
      className="tier-card"
      style={{
        borderTop: `4px solid ${tier.color}`,
        background: `linear-gradient(135deg, ${tier.color}10 0%, var(--gray-50) 100%)`
      }}
    >
      <div className="tier-header">
        <span className="tier-icon">{tier.icon}</span>
        <h3 className="tier-name">{tier.name}</h3>
      </div>
      
      <div className="tier-requirement">
        <span className="text-secondary">Min. Spend:</span>
        <strong>${tier.minSpend?.toLocaleString()}</strong>
      </div>

      <div className="tier-stats">
        <div className="stat">
          <div className="stat-value">
            {customers.filter(c => c.tier_id === tier.id).length}
          </div>
          <div className="stat-label">Members</div>
        </div>
      </div>

      <div className="tier-actions">
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => {
            setSelectedTier(tier);
            setBenefitsForm(tier.benefits || {});
            setShowBenefitsModal(true);
          }}
        >
          🎁 Benefits
        </button>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => {
            setSelectedTier(tier);
            setTierForm({ ...tier });
            setShowTierModal(true);
          }}
        >
          ✏️ Edit
        </button>
      </div>
    </div>
  );

  // Render customer row
  const renderCustomerRow = (customer) => {
    const tier = tiers.find(t => t.id === customer.tier_id) || tiers[0];
    
    return (
      <tr key={customer.id}>
        <td>
          <div className="flex align-center gap-2">
            <div className="avatar-sm">{customer.name?.charAt(0) || '?'}</div>
            <div>
              <div className="font-medium">{customer.name || 'Unknown'}</div>
              <div className="text-xs text-secondary">{customer.email}</div>
            </div>
          </div>
        </td>
        <td>
          <span 
            className="badge"
            style={{ background: tier.color, color: tier.color === '#ffd700' ? '#000' : '#fff' }}
          >
            {tier.icon} {tier.name}
          </span>
        </td>
        <td>${customer.total_spend?.toLocaleString() || 0}</td>
        <td>{customer.points?.toLocaleString() || 0}</td>
        <td>{customer.orders_count || 0}</td>
        <td>
          {customer.next_tier_progress !== undefined && (
            <div className="progress-container">
              <div className="progress-bar-sm">
                <div 
                  className="progress-fill"
                  style={{ width: `${customer.next_tier_progress}%` }}
                />
              </div>
              <span className="text-xs">{customer.next_tier_progress}%</span>
            </div>
          )}
        </td>
        <td>
          <select
            className="form-control form-control-sm"
            value={customer.tier_id || ''}
            onChange={(e) => handleUpdateCustomerTier(customer.id, e.target.value)}
          >
            {tiers.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </td>
      </tr>
    );
  };

  // Render benefits modal
  const renderBenefitsModal = () => (
    <div className="modal-overlay" onClick={() => setShowBenefitsModal(false)}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {selectedTier?.icon} Configure {selectedTier?.name} Benefits
          </h3>
          <button className="btn-icon" onClick={() => setShowBenefitsModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="benefits-grid">
            {BENEFIT_TYPES.map(benefit => (
              <div key={benefit.id} className="benefit-config">
                <div className="benefit-header">
                  <span className="benefit-icon">{benefit.icon}</span>
                  <span className="benefit-name">{benefit.name}</span>
                </div>
                <div className="benefit-input">
                  {benefit.unit === 'boolean' ? (
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={benefitsForm[benefit.id]?.enabled || false}
                        onChange={e => setBenefitsForm({
                          ...benefitsForm,
                          [benefit.id]: { enabled: e.target.checked }
                        })}
                      />
                      <span className="slider"></span>
                    </label>
                  ) : (
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        value={benefitsForm[benefit.id]?.value || ''}
                        onChange={e => setBenefitsForm({
                          ...benefitsForm,
                          [benefit.id]: { value: e.target.value, enabled: true }
                        })}
                      />
                      <span className="input-suffix">{benefit.unit}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowBenefitsModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleConfigureBenefits}>
            💾 Save Benefits
          </button>
        </div>
      </div>
    </div>
  );

  // Render tier modal
  const renderTierModal = () => (
    <div className="modal-overlay" onClick={() => setShowTierModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{selectedTier ? 'Edit Tier' : 'Create New Tier'}</h3>
          <button className="btn-icon" onClick={() => setShowTierModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Tier Name</label>
            <input
              type="text"
              className="form-control"
              value={tierForm.name}
              onChange={e => setTierForm({ ...tierForm, name: e.target.value })}
              placeholder="e.g., Gold"
            />
          </div>
          <div className="form-group">
            <label>Minimum Spend ($)</label>
            <input
              type="number"
              className="form-control"
              value={tierForm.minSpend}
              onChange={e => setTierForm({ ...tierForm, minSpend: e.target.value })}
              placeholder="e.g., 1000"
            />
          </div>
          <div className="grid-2-col gap-4">
            <div className="form-group">
              <label>Color</label>
              <input
                type="color"
                className="form-control form-control-color"
                value={tierForm.color}
                onChange={e => setTierForm({ ...tierForm, color: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Icon</label>
              <select
                className="form-control"
                value={tierForm.icon}
                onChange={e => setTierForm({ ...tierForm, icon: e.target.value })}
              >
                <option value="⭐">⭐ Star</option>
                <option value="🥉">🥉 Bronze</option>
                <option value="🥈">🥈 Silver</option>
                <option value="🥇">🥇 Gold</option>
                <option value="💎">💎 Diamond</option>
                <option value="👑">👑 Crown</option>
                <option value="🏆">🏆 Trophy</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowTierModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => {
              if (selectedTier) {
                setTiers(tiers.map(t => t.id === selectedTier.id ? { ...t, ...tierForm } : t));
              } else {
                setTiers([...tiers, { ...tierForm, id: `tier_${Date.now()}` }]);
              }
              setShowTierModal(false);
            }}
          >
            💾 Save Tier
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && !tiers.length) {
    return (
      <div>
        <div className="loading-spinner">Loading VIP Program...</div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">VIP Program</span>
      </nav>

      <div className="card">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              👑 VIP Program
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Manage customer loyalty tiers, benefits, and rewards
            </p>
          </div>
          <div className="flex gap-2">
            {programStatus?.active ? (
              <span className="badge success" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                🟢 Program Active
              </span>
            ) : (
              <button className="btn btn-primary" onClick={handleLaunchProgram}>
                🚀 Launch VIP Program
              </button>
            )}
          </div>
        </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-danger mb-4">
          {error}
          <button className="btn-icon ml-auto" onClick={() => setError(null)}>✕</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success mb-4">
          {success}
          <button className="btn-icon ml-auto" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs mb-4">
        {['overview', 'tiers', 'members', 'analytics'].map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' && '📊 Overview'}
            {tab === 'tiers' && '🏆 Tiers'}
            {tab === 'members' && '👥 Members'}
            {tab === 'analytics' && '📈 Analytics'}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Stats */}
          <div className="stats-grid-4 mb-4">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-value">{programStatus?.total_members || customers.length}</div>
                <div className="stat-label">Total VIP Members</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <div className="stat-value">
                  ${analytics?.total_vip_revenue?.toLocaleString() || '0'}
                </div>
                <div className="stat-label">VIP Revenue</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                <div className="stat-value">{analytics?.avg_order_value || '0'}%</div>
                <div className="stat-label">Avg. Order Increase</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔄</div>
              <div className="stat-content">
                <div className="stat-value">{analytics?.retention_rate || '0'}%</div>
                <div className="stat-label">Retention Rate</div>
              </div>
            </div>
          </div>

          {/* Tier Overview */}
          <div className="inner-card mb-4">
            <h3 className="card-title">Tier Distribution</h3>
            <div className="tier-distribution">
              {tiers.map(tier => {
                const memberCount = customers.filter(c => c.tier_id === tier.id).length;
                const percentage = customers.length > 0 
                  ? Math.round((memberCount / customers.length) * 100) 
                  : 0;
                
                return (
                  <div key={tier.id} className="tier-bar-container">
                    <div className="tier-bar-label">
                      <span>{tier.icon} {tier.name}</span>
                      <span>{memberCount} ({percentage}%)</span>
                    </div>
                    <div className="tier-bar">
                      <div 
                        className="tier-bar-fill"
                        style={{ 
                          width: `${percentage}%`,
                          background: tier.color
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid-3-col">
            <div className="inner-card text-center cursor-pointer hover-lift" onClick={() => setActiveTab('tiers')}>
              <span style={{ fontSize: '3rem', display: 'block', marginBottom: '0.75rem' }}>🏆</span>
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Configure Tiers</h4>
              <p className="text-secondary" style={{ margin: 0 }}>Set up tier levels and requirements</p>
            </div>
            <div className="inner-card text-center cursor-pointer hover-lift" onClick={() => setActiveTab('members')}>
              <span style={{ fontSize: '3rem', display: 'block', marginBottom: '0.75rem' }}>👥</span>
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Manage Members</h4>
              <p className="text-secondary" style={{ margin: 0 }}>View and manage VIP customers</p>
            </div>
            <div className="inner-card text-center cursor-pointer hover-lift" onClick={() => setActiveTab('analytics')}>
              <span style={{ fontSize: '3rem', display: 'block', marginBottom: '0.75rem' }}>📈</span>
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>View Analytics</h4>
              <p className="text-secondary" style={{ margin: 0 }}>Program performance insights</p>
            </div>
          </div>
        </>
      )}

      {/* Tiers Tab */}
      {activeTab === 'tiers' && (
        <>
          <div className="flex justify-between align-center mb-4">
            <h3>VIP Tiers</h3>
            <div className="flex gap-2">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setSelectedTier(null);
                  setTierForm({ name: '', minSpend: '', color: '#6b7280', icon: '⭐', benefits: {} });
                  setShowTierModal(true);
                }}
              >
                ➕ Add Tier
              </button>
              <button className="btn btn-primary" onClick={handleConfigureTiers}>
                💾 Save Configuration
              </button>
            </div>
          </div>

          <div className="tiers-grid">
            {tiers.map((tier, index) => renderTierCard(tier, index))}
          </div>

          <div className="inner-card mt-4">
            <h4 className="card-title">Tier Progression Path</h4>
            <div className="progression-path">
              {tiers.map((tier, index) => (
                <React.Fragment key={tier.id}>
                  <div className="progression-node" style={{ borderColor: tier.color }}>
                    <span className="node-icon">{tier.icon}</span>
                    <span className="node-name">{tier.name}</span>
                    <span className="node-spend">${tier.minSpend?.toLocaleString()}</span>
                  </div>
                  {index < tiers.length - 1 && (
                    <div className="progression-arrow">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div className="inner-card">
          <div className="flex justify-between align-center mb-4">
            <h3 className="card-title" style={{ marginBottom: 0 }}>VIP Members</h3>
            <div className="flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search members..."
                style={{ width: '250px' }}
              />
              <button className="btn btn-secondary">Export</button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Tier</th>
                  <th>Total Spend</th>
                  <th>Points</th>
                  <th>Orders</th>
                  <th>Next Tier</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.length > 0 ? (
                  customers.map(renderCustomerRow)
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-secondary py-4">
                      No VIP members yet. Customers will appear here once they qualify.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="grid-2-col">
          <div className="inner-card">
            <h3 className="card-title">Revenue by Tier</h3>
            <div className="chart-placeholder">
              {tiers.map(tier => (
                <div key={tier.id} className="revenue-bar">
                  <span>{tier.icon} {tier.name}</span>
                  <div className="bar-container">
                    <div 
                      className="bar"
                      style={{ 
                        width: `${Math.random() * 100}%`,
                        background: tier.color
                      }}
                    />
                  </div>
                  <span>${Math.floor(Math.random() * 50000).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="inner-card">
            <h3 className="card-title">Benefit Utilization</h3>
            <div className="benefit-stats">
              {BENEFIT_TYPES.slice(0, 5).map(benefit => (
                <div key={benefit.id} className="benefit-stat">
                  <span>{benefit.icon} {benefit.name}</span>
                  <div className="progress-bar-sm">
                    <div 
                      className="progress-fill"
                      style={{ width: `${Math.random() * 100}%` }}
                    />
                  </div>
                  <span>{Math.floor(Math.random() * 100)}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="inner-card">
            <h3 className="card-title">Member Growth</h3>
            <div className="growth-stats">
              <div className="growth-item">
                <span className="growth-label">This Month</span>
                <span className="growth-value success">+{Math.floor(Math.random() * 50)}</span>
              </div>
              <div className="growth-item">
                <span className="growth-label">Last Month</span>
                <span className="growth-value">+{Math.floor(Math.random() * 50)}</span>
              </div>
              <div className="growth-item">
                <span className="growth-label">YTD</span>
                <span className="growth-value success">+{Math.floor(Math.random() * 500)}</span>
              </div>
            </div>
          </div>

          <div className="inner-card">
            <h3 className="card-title">Tier Upgrades</h3>
            <div className="upgrades-list">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="upgrade-item">
                  <div className="upgrade-customer">Customer #{1000 + i}</div>
                  <div className="upgrade-path">
                    <span className="badge secondary">Silver</span>
                    <span>→</span>
                    <span className="badge" style={{ background: '#ffd700', color: '#000' }}>Gold</span>
                  </div>
                  <div className="upgrade-date text-xs text-secondary">
                    {new Date(Date.now() - i * 86400000).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showTierModal && renderTierModal()}
      {showBenefitsModal && renderBenefitsModal()}

      <style>{`
        .stats-grid-4 {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }
        @media (max-width: 1200px) {
          .stats-grid-4 {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 640px) {
          .stats-grid-4 {
            grid-template-columns: 1fr;
          }
        }
        .grid-3-col {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }
        @media (max-width: 900px) {
          .grid-3-col {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 640px) {
          .grid-3-col {
            grid-template-columns: 1fr;
          }
        }
        .grid-2-col {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
        @media (max-width: 768px) {
          .grid-2-col {
            grid-template-columns: 1fr;
          }
        }
        .tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid var(--border-color);
          margin-bottom: 1.5rem;
        }
        .tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          color: var(--text-secondary);
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
        }
        .tab.active {
          color: var(--primary-600);
          border-bottom-color: var(--primary-600);
          font-weight: 600;
        }
        .tiers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }
        .tier-card {
          background: var(--bg-secondary);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          transition: transform 0.2s;
          border: 1px solid var(--border-color);
        }
        .tier-card:hover {
          transform: translateY(-2px);
        }
        .tier-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .tier-icon {
          font-size: 2rem;
        }
        .tier-name {
          font-size: 1.25rem;
          margin: 0;
          color: var(--text-primary);
        }
        .tier-requirement {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem;
          background: var(--bg-primary);
          border-radius: var(--radius-sm);
          margin-bottom: 1rem;
          border: 1px solid var(--border-color);
          color: var(--text-primary);
        }
        .tier-stats {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .tier-actions {
          display: flex;
          gap: 0.5rem;
        }
        .stat-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: var(--bg-primary);
          padding: 1.5rem;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--border-color);
        }
        .stat-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }
        .stat-content {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        }
        .stat-value {
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: bold;
        }
        .stat-label {
          color: var(--text-secondary);
          font-size: 0.875rem;
        }
        .tier-distribution {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .tier-bar-container {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .tier-bar-label {
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
          color: var(--text-primary);
        }
        .tier-bar {
          height: 24px;
          background: var(--bg-tertiary);
          border-radius: 12px;
          overflow: hidden;
        }
        .tier-bar-fill {
          height: 100%;
          border-radius: 12px;
          transition: width 0.3s;
        }
        .progression-path {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 2rem;
          overflow-x: auto;
        }
        .progression-node {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1rem;
          border: 3px solid;
          border-radius: var(--radius-lg);
          min-width: 100px;
          background: var(--bg-primary);
        }
        .node-icon {
          font-size: 1.5rem;
        }
        .node-name {
          font-weight: 600;
          color: var(--text-primary);
        }
        .node-spend {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }
        .progression-arrow {
          font-size: 1.5rem;
          color: var(--text-muted);
        }
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
        .benefit-config {
          padding: 1rem;
          background: var(--bg-secondary);
          border-radius: var(--radius-sm);
          border: 1px solid var(--border-color);
        }
        .benefit-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          color: var(--text-primary);
        }
        .benefit-icon {
          font-size: 1.25rem;
        }
        .input-group {
          display: flex;
          align-items: center;
        }
        .input-suffix {
          padding: 0.5rem;
          background: var(--bg-tertiary);
          border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
          color: var(--text-secondary);
        }
        .switch {
          position: relative;
          width: 50px;
          height: 26px;
        }
        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: var(--bg-tertiary);
          transition: 0.3s;
          border-radius: 26px;
        }
        .slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 3px;
          bottom: 3px;
          background-color: var(--bg-primary);
          transition: 0.3s;
          border-radius: 50%;
        }
        input:checked + .slider {
          background-color: var(--primary-500);
        }
        input:checked + .slider:before {
          transform: translateX(24px);
        }
        .progress-bar-sm {
          height: 6px;
          background: var(--bg-tertiary);
          border-radius: 3px;
          flex: 1;
        }
        .progress-fill {
          height: 100%;
          background: var(--primary-500);
          border-radius: 3px;
        }
        .progress-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .avatar-sm {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--primary-100);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: var(--primary-700);
        }
        .hover-lift {
          transition: transform 0.2s;
          cursor: pointer;
        }
        .hover-lift:hover {
          transform: translateY(-4px);
        }
        .revenue-bar, .benefit-stat {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .bar-container {
          flex: 1;
          height: 20px;
          background: var(--bg-tertiary);
          border-radius: 10px;
          overflow: hidden;
        }
        .bar {
          height: 100%;
          border-radius: 10px;
        }
        .growth-stats {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .growth-item {
          display: flex;
          justify-content: space-between;
          padding: 1rem;
          background: var(--bg-secondary);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
        }
        .growth-value.success {
          color: var(--success-600);
          font-weight: 600;
        }
        .upgrades-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .upgrade-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: var(--bg-secondary);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
        }
        .upgrade-path {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        /* Utility classes */
        .mb-4 {
          margin-bottom: 1.5rem;
        }
        .mt-4 {
          margin-top: 1.5rem;
        }
        .gap-2 {
          gap: 0.5rem;
        }
        .gap-4 {
          gap: 1rem;
        }
        .flex {
          display: flex;
        }
        .justify-between {
          justify-content: space-between;
        }
        .align-center {
          align-items: center;
        }
        .text-center {
          text-align: center;
        }
        .text-secondary {
          color: var(--text-secondary);
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .card-title {
          color: var(--text-primary);
          font-size: 1.125rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }
        .inner-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
        }
      `}</style>
      </div>
    </div>
  );
};

export default VIPProgram;
